/**
 * Ajustes → Declaración responsable del sistema informático de facturación (Orden HAC/1177/2024,
 * art. 15). Visible para cualquier usuario: la Orden pide que esté disponible de forma legible
 * dentro del propio sistema. Muestra la de la versión que funciona, firmada o pendiente de firma.
 */
import { useEffect, useState } from 'react'
import { AlertTriangle, CheckCircle2, Download, FileText, Loader2 } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import type { VerifactuDeclarationDto } from '@toga/shared'

const ddmmyyyy = (day: string) => day.split('-').reverse().join('/')

export default function VerifactuDeclarationPage() {
  const [decl, setDecl] = useState<VerifactuDeclarationDto | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .get<VerifactuDeclarationDto>('/verifactu/declaration')
      .then(setDecl)
      .catch((err) =>
        setError(err instanceof ApiError ? 'No se pudo cargar la declaración' : String(err))
      )
  }, [])

  if (error) return <div className="text-sm text-rose-300">{error}</div>
  if (!decl) {
    return (
      <div className="t-muted flex items-center gap-2">
        <Loader2 size={16} className="animate-spin" /> Cargando…
      </div>
    )
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-serif font-semibold t-primary flex items-center gap-2">
            <FileText size={20} className="text-toga-300" /> Declaración responsable
          </h2>
          <p className="text-xs t-muted mt-0.5">
            Toga {decl.version} · módulo @jr2/contable {decl.moduleVersion}
          </p>
        </div>
        <div className="flex gap-2">
          <a
            href="/api/verifactu/declaration.pdf"
            target="_blank"
            rel="noreferrer"
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover flex items-center gap-1.5"
          >
            <Download size={14} /> PDF
          </a>
          <a
            href="/api/verifactu/declaration.txt"
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover flex items-center gap-1.5"
          >
            <Download size={14} /> TXT
          </a>
        </div>
      </header>

      {decl.state.status === 'signed' ? (
        <div className="surface p-3 text-xs rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-200 flex items-center gap-2">
          <CheckCircle2 size={14} /> Firmada el {ddmmyyyy(decl.state.signedAt)} en {decl.place}.
        </div>
      ) : (
        <div className="surface p-3 text-xs rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-200 flex items-start gap-2">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <span>
            <strong>Pendiente de firma.</strong> {decl.state.reason}
          </span>
        </div>
      )}

      <article className="surface p-5 space-y-3">
        <h3 className="t-primary font-semibold text-sm uppercase tracking-wide">{decl.title}</h3>
        <dl className="space-y-2 text-sm">
          {decl.items.map((item) => (
            <div key={item.letter}>
              <dt className="text-xs t-muted">
                {item.letter}) {item.label}
              </dt>
              <dd className="t-primary whitespace-pre-wrap">{item.value}</dd>
            </div>
          ))}
        </dl>
        {decl.annex.length > 0 && (
          <div className="pt-3 border-t border-base space-y-2">
            <h4 className="t-primary font-semibold text-sm">Anexo</h4>
            {decl.annex.map((section) => (
              <div key={section.title} className="text-sm">
                <div className="text-xs t-muted">{section.title}</div>
                <div className="t-primary whitespace-pre-wrap">{section.text}</div>
              </div>
            ))}
          </div>
        )}
      </article>
    </div>
  )
}
